import { Card, Grid, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import CustomButton from "../../components/CustomButton";
import CustomModal from "../../components/CustomModal/CustomModal";
import CustomTextField from "../../components/CustomTextField";
import CustomSelect from "../../components/Select/Select";
import { payAmountByPaymentId } from "../../redux/reducers/clinicSlice";
import {
  getBorderColor,
  getPetShopRecordBasedType,
  getPharmacyRecordBasedType,
  getTaxAndSubTotalAndTotal,
  getTreatmentRecordBasedType,
  roundWithSignedDiff,
} from "../../util/function";
import PaymentModeDropdown from "./PaymentModeDropdown";

const initValues = {
  invoiceNumber: [],
  treatAmt: "",
  treatPaymentMode: null,
  pharAmt: "",
  pharPaymentMode: null,
  petShopAmt: "",
  petShopPaymentMode: null,
};

const CommonPayModal = ({
  modVsble = false,
  onCloseMod = () => {},
  selectedBill = null,
  billsToShow = [],
  selectedTab = null,
}) => {
  const dispatch = useDispatch();
  const [modValues, setModValues] = useState(initValues);
  const [modErrors, setModErrors] = useState({});

  const treatRecords = getTreatmentRecordBasedType(
    selectedBill?.paymentDetails,
  );
  const pharRecords = getPharmacyRecordBasedType(selectedBill?.paymentDetails);
  const petShopRecords = getPetShopRecordBasedType(
    selectedBill?.paymentDetails,
  );

  const treatTotal = roundWithSignedDiff(
    getTaxAndSubTotalAndTotal(treatRecords)?.subTotal,
  )?.rounded;
  const pharTotal = roundWithSignedDiff(
    getTaxAndSubTotalAndTotal(pharRecords)?.subTotal,
  )?.rounded;
  const petShopTotal = roundWithSignedDiff(
    getTaxAndSubTotalAndTotal(petShopRecords)?.subTotal,
  )?.rounded;

  const config = {
    TREATMENT: {
      total: treatTotal,
      paid: selectedBill?.amtPaid ?? 0,
      invoiceKey: "treatmentInvoiceNumber",
      toShow:
        treatRecords?.length > 0 &&
        selectedBill?.treatmentInvoiceNumber?.length > 0,
    },
    PHARMACY: {
      total: pharTotal,
      paid: selectedBill?.pharmacyAmtPaid ?? 0,
      invoiceKey: "invoiceNumbers",
      toShow:
        pharRecords?.length > 0 && selectedBill?.invoiceNumbers?.length > 0,
    },
    PETSHOP: {
      total: petShopTotal,
      paid: selectedBill?.petShopAmtPaid ?? 0,
      invoiceKey: "petShopInvoiceNumber",
      toShow:
        petShopRecords?.length > 0 &&
        selectedBill?.petShopInvoiceNumber?.length > 0,
    },
  };

  useEffect(() => {
    if (selectedTab) {
      const { total, paid, invoiceKey } = config[selectedTab];
      const balanceDue = roundWithSignedDiff(
        Number(total) - Number(paid ?? 0),
      )?.rounded;
      const reqObj = {
        name: `${selectedBill?.[invoiceKey] ?? ""}, ₹${balanceDue}, ${moment(
          new Date(selectedBill?.createdDate),
        ).format("DD MMM YYYY")}`,
        value: String(selectedBill?.paymentId ?? ""),
        paymentId: selectedBill?.paymentId,
        total: balanceDue,
        advanceAmount: selectedBill?.advanceAmount,
        tabName: selectedTab,
      };

      setModValues({
        invoiceNumber: [reqObj],
        [selectedTab === "TREATMENT"
          ? "treatAmt"
          : selectedTab === "PHARMACY"
            ? "pharAmt"
            : "petShopAmt"]: balanceDue,
      });
    }
  }, [selectedTab]);

  const getInvoiceList = () => {
    return (billsToShow ?? [])
      ?.filter((type) => config?.[type] && config?.[type]?.toShow)
      ?.map((type) => {
        const { total, paid, invoiceKey } = config[type];

        const balanceDue = roundWithSignedDiff(
          Number(total) - Number(paid ?? 0),
        )?.rounded;

        return {
          name: `${selectedBill?.[invoiceKey] ?? ""}, ₹${balanceDue}, ${moment(
            new Date(selectedBill?.createdDate),
          ).format("DD MMM YYYY")}`,
          value: String(selectedBill?.paymentId ?? ""),
          paymentId: selectedBill?.paymentId,
          total: balanceDue,
          advanceAmount: selectedBill?.advanceAmount,
          tabName: type,
        };
      });
  };

  const amtConfig = {
    treatAmt: {
      total: Number(treatTotal),
      //  - Number(selectedBill?.amtPaid ?? 0),
      paidKey: "amtPaid",
      paymentModeKey: "treatPaymentMode",
    },
    pharAmt: {
      total: Number(pharTotal),
      //  - Number(selectedBill?.pharmacyAmtPaid ?? 0),
      paidKey: "pharmacyAmtPaid",
      paymentModeKey: "pharPaymentMode",
    },
    petShopAmt: {
      total: Number(petShopTotal),
      //  - Number(selectedBill?.petShopAmtPaid ?? 0),
      paidKey: "petShopAmtPaid",
      paymentModeKey: "petShopPaymentMode",
    },
  };

  const onChangeModValue = (name, value) => {
    const reqValue =
      typeof value === "object"
        ? Array.isArray(value)
          ? value
          : value?.value
        : value;
    const reqObj = { ...modValues, [name]: value };
    const errs = {
      ...modErrors,
      [name]:
        (typeof reqValue === "number" ? reqValue?.toString() : reqValue)
          ?.length > 0
          ? ""
          : "This field is required",
    };
    if (name === "invoiceNumber") {
      if (name === "invoiceNumber") {
        const config = {
          TREATMENT: { key: "treatAmt" },
          PHARMACY: { key: "pharAmt" },
          PETSHOP: { key: "petShopAmt" },
        };
        Object.entries(config)?.forEach(([tab, { key }]) => {
          const item = value?.find((li) => li?.tabName === tab);
          if (!item) return;
          reqObj[key] =
            modValues?.[key]?.length > 0 ? modValues?.[key] : item?.total;
        });
      }
    }
    if (amtConfig?.[name]) {
      const validateAmount = (key, total, paidKey, paymentModeKey) => {
        const val = Number(value);
        const paid = Number(selectedBill?.[paidKey] ?? 0);
        const balance = Number(total) - paid;

        let error = "";

        if (modValues?.[paymentModeKey]?.value === "Advance Adjustment") {
          if (val < 0) error = "Amount must be positive";
          else if (val > Number(selectedBill?.advanceAmount ?? 0)) {
            error = "Insufficient credit balance";
          }
        } else {
          if (val < 0) error = "Amount must be positive";
        }

        if (val > balance) {
          error = "Amount must be less than or equal to balance due";
        }
        if (!val) {
          error = "This field is required";
        }
        errs[key] = error;
      };
      const { total, paidKey, paymentModeKey } = amtConfig[name];
      validateAmount(name, total, paidKey, paymentModeKey);
    }
    setModErrors(errs);
    setModValues(reqObj);
  };

  const onPay = () => {
    const errs = { ...modErrors };
    if (modValues?.invoiceNumber?.length === 0)
      errs.invoiceNumber = "This field is required";
    if (modValues?.invoiceNumber?.some((li) => li?.tabName === "TREATMENT")) {
      const val = Number(modValues?.treatAmt ?? 0);
      const advance = Number(selectedBill?.advanceAmount ?? 0);
      const balance = Number(treatTotal) - Number(selectedBill?.amtPaid ?? 0);
      let error = "";
      if (modValues?.treatPaymentMode?.value === "Advance Adjustment") {
        if (val < 0) {
          error = "Amount must be positive";
        } else if (val > advance) {
          error = "Insufficient credit balance";
        }
      } else {
        if (val < 0) {
          error = "Amount must be positive";
        }
      }
      if (val > balance) {
        error = "Amount must be less than or equal to balance due";
      }
      if (!val) error = "This field is required";
      errs.treatAmt = error;

      errs.treatPaymentMode = !modValues?.treatPaymentMode?.value
        ? "This field is required"
        : "";
    }

    if (modValues?.invoiceNumber?.some((li) => li?.tabName === "PHARMACY")) {
      const val = Number(modValues?.pharAmt ?? 0);
      const advance = Number(selectedBill?.advanceAmount ?? 0);
      const balance =
        Number(pharTotal) - Number(selectedBill?.pharmacyAmtPaid ?? 0);
      let error = "";
      if (modValues?.pharPaymentMode?.value === "Advance Adjustment") {
        if (val < 0) {
          error = "Amount must be positive";
        } else if (val > advance) {
          error = "Insufficient credit balance";
        }
      } else {
        if (val < 0) {
          error = "Amount must be positive";
        }
      }
      if (val > balance) {
        error = "Amount must be less than or equal to balance due";
      }
      if (!val) error = "This field is required";
      errs.pharAmt = error;

      errs.pharPaymentMode = !modValues?.pharPaymentMode?.value
        ? "This field is required"
        : "";
    }

    if (modValues?.invoiceNumber?.some((li) => li?.tabName === "PETSHOP")) {
      const val = Number(modValues?.petShopAmt ?? 0);
      const advance = Number(selectedBill?.advanceAmount ?? 0);
      const balance =
        Number(petShopTotal) - Number(selectedBill?.petShopAmtPaid ?? 0);
      let error = "";
      if (modValues?.petShopPaymentMode?.value === "Advance Adjustment") {
        if (val < 0) {
          error = "Amount must be positive";
        } else if (val > advance) {
          error = "Insufficient credit balance";
        }
      } else {
        if (val < 0) {
          error = "Amount must be positive";
        }
      }
      if (val > balance) {
        error = "Amount must be less than or equal to balance due";
      }
      if (!val) error = "This field is required";
      errs.petShopAmt = error;

      errs.petShopPaymentMode = !modValues?.petShopPaymentMode?.value
        ? "This field is required"
        : "";
    }
    if (Object.values(errs)?.some((err) => err?.length > 0)) {
      return setModErrors(errs);
    }
    const data = [];
    if (modValues?.invoiceNumber?.some((li) => li?.tabName === "TREATMENT")) {
      data.push({
        mode: modValues?.treatPaymentMode?.value,
        amount: Number(modValues?.treatAmt ?? 0),
        tabName: "TREATMENT",
        invoiceNo: selectedBill?.treatmentInvoiceNumber ?? "",
        status:
          selectedBill?.checkOut === "Y" || selectedBill?.checkOut === "P"
            ? "APPLIED"
            : "UNAPPLIED",
      });
    }
    if (modValues?.invoiceNumber?.some((li) => li?.tabName === "PHARMACY")) {
      data.push({
        mode: modValues?.pharPaymentMode?.value,
        amount: Number(modValues?.pharAmt ?? 0),
        tabName: "PHARMACY",
        invoiceNo: selectedBill?.invoiceNumbers ?? "",
        status:
          selectedBill?.pharmacyCheckout === "Y" ||
          selectedBill?.pharmacyCheckout === "P"
            ? "APPLIED"
            : "UNAPPLIED",
      });
    }
    if (modValues?.invoiceNumber?.some((li) => li?.tabName === "PETSHOP")) {
      data.push({
        mode: modValues?.petShopPaymentMode?.value,
        amount: Number(modValues?.petShopAmt ?? 0),
        tabName: "PETSHOP",
        invoiceNo: selectedBill?.petShopInvoiceNumber ?? "",
        status:
          selectedBill?.petShopCheckout === "Y" ||
          selectedBill?.petShopCheckout === "P"
            ? "APPLIED"
            : "UNAPPLIED",
      });
    }
    if (!selectedBill?.paymentId) return;
    dispatch(
      payAmountByPaymentId({ paymentId: selectedBill?.paymentId, data }),
    ).then((res) => {
      if (res?.payload) {
        onCloseMod("apiSuccess");
      }
    });
  };

  return (
    <CustomModal
      open={modVsble}
      onClose={() => onCloseMod("modClosed")}
      header="Pay"
      rightModal
      modalWidth={30}
    >
      <div className="scroll-80vh">
        <div className="flex-center">
          <Card
            sx={{
              borderRadius: 1,
              padding: 2,
              borderTopColor: `${getBorderColor(selectedBill?.status)}`,
            }}
            className="pay-card"
          >
            <div className="flex-row">
              <div className="w50Per">
                <Typography className="fs12 txt-semi-bold gray14">
                  Pet Name
                </Typography>
                <Typography className="fs14 txt-semi-bold black">
                  {selectedBill?.petName}
                </Typography>
              </div>
              <div className="w50Per">
                <Typography className="fs12 txt-semi-bold gray14">
                  Parent Name
                </Typography>
                <Typography className="fs14 txt-semi-bold black">
                  {selectedBill?.userName}
                </Typography>
              </div>
            </div>
            <div className="mh20Min">
              <div className="gry-hor-split3 mv5" />
            </div>
            <div className="mt10">
              <Typography className="txt-regular fs14 gray7">
                {moment(new Date(selectedBill?.createdDate)).format(
                  "DD MMM, YYYY",
                )}
              </Typography>
            </div>
            {selectedBill?.appointmentType ? (
              <div
                className={`conttype ${
                  selectedBill?.appointmentType === "Physical"
                    ? "card-con-blue-back"
                    : "virtual-bg-color"
                }`}
              >
                {selectedBill?.appointmentType}
              </div>
            ) : null}
          </Card>
        </div>
        <Grid container spacing={2} className="ph20">
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div className="txt-semi-bold fs14 light-grey">
              Invoice Number <span className="red-color">*</span>
            </div>
            <CustomSelect
              list={getInvoiceList()}
              name="invoiceNumber"
              handleChange={(e) => {
                onChangeModValue("invoiceNumber", e?.target?.value);
              }}
              value={modValues?.invoiceNumber ?? []}
              helperText={modErrors?.invoiceNumber}
              error={!!modErrors?.invoiceNumber}
              multiSelectSearchCheck
            />
          </Grid>

          {billsToShow?.includes("TREATMENT") &&
          modValues?.invoiceNumber?.some(
            (li) => li?.tabName === "TREATMENT",
          ) ? (
            <>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography className="fs14 txt-semi-bold">
                  Treatment
                </Typography>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="bil-his-box mtMin5">
                  <div className="flex-row">
                    <div className="flex-start">
                      <Typography className="fs14 font-medium black">
                        {selectedBill?.treatmentInvoiceNumber ?? ""}
                      </Typography>
                    </div>
                    <div className="flex1-end">
                      <Typography className="txt-semi-bold fs14 orange3">
                        Rs{" "}
                        {Number(
                          Number(treatTotal) -
                            Number(selectedBill?.amtPaid ?? 0),
                        )?.toFixed(2)}
                      </Typography>
                    </div>
                  </div>
                </div>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <CustomTextField
                  label="Amount"
                  name="treatAmt"
                  fullWidth
                  handleChange={(e) =>
                    onChangeModValue("treatAmt", e?.target?.value)
                  }
                  value={modValues?.treatAmt}
                  helperText={modErrors?.treatAmt}
                  error={!!modErrors?.treatAmt}
                  labelTop
                  type="number"
                />
              </Grid>

              <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                <div className="font-medium fs14 black2">Payment Mode</div>
                <PaymentModeDropdown
                  list={[]}
                  value={modValues?.treatPaymentMode}
                  error={!!modErrors?.treatPaymentMode}
                  helperText={modErrors?.treatPaymentMode}
                  onChange={(e, value) =>
                    onChangeModValue("treatPaymentMode", e)
                  }
                  name="treatPaymentMode"
                  label=""
                />
              </Grid>
              {/* {modValues?.treatPaymentMode?.value === "Advance Adjustment" ? ( */}
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography className="txt-semi-bold fs14 orange5">
                  Available Credit:{" "}
                  {Number(selectedBill?.advanceAmount)?.toFixed(2)}
                </Typography>
              </Grid>
              {/* ) : null} */}
            </>
          ) : null}
          {billsToShow?.includes("PHARMACY") &&
          modValues?.invoiceNumber?.some((li) => li?.tabName === "PHARMACY") ? (
            <>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography className="fs14 txt-semi-bold">Pharmacy</Typography>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="bil-his-box mtMin5">
                  <div className="flex-row">
                    <div className="flex-start">
                      <Typography className="fs14 font-medium black">
                        {selectedBill?.invoiceNumbers ?? ""}
                      </Typography>
                    </div>
                    <div className="flex1-end">
                      <Typography className="txt-semi-bold fs14 orange3">
                        Rs{" "}
                        {Number(
                          Number(pharTotal) -
                            Number(selectedBill?.pharmacyAmtPaid ?? 0),
                        )?.toFixed(2)}
                      </Typography>
                    </div>
                  </div>
                </div>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <CustomTextField
                  label="Amount"
                  name="pharAmt"
                  fullWidth
                  handleChange={(e) =>
                    onChangeModValue("pharAmt", e?.target?.value)
                  }
                  value={modValues?.pharAmt}
                  helperText={modErrors?.pharAmt}
                  error={!!modErrors?.pharAmt}
                  labelTop
                  type="number"
                />
              </Grid>

              <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                <div className="font-medium fs14 black2">Payment Mode</div>
                <PaymentModeDropdown
                  list={[]}
                  value={modValues?.pharPaymentMode}
                  error={!!modErrors?.pharPaymentMode}
                  helperText={modErrors?.pharPaymentMode}
                  onChange={(e, value) =>
                    onChangeModValue("pharPaymentMode", e)
                  }
                  name="pharPaymentMode"
                  label=""
                />
              </Grid>
              {/* {modValues?.pharPaymentMode?.value === "Advance Adjustment" ? ( */}
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography className="txt-semi-bold fs14 orange5">
                  Available Credit:{" "}
                  {Number(selectedBill?.advanceAmount)?.toFixed(2)}
                </Typography>
              </Grid>
              {/* ) : null} */}
            </>
          ) : null}
          {billsToShow?.includes("PETSHOP") &&
          modValues?.invoiceNumber?.some((li) => li?.tabName === "PETSHOP") ? (
            <>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography className="fs14 txt-semi-bold">Pet Shop</Typography>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="bil-his-box mtMin5">
                  <div className="flex-row">
                    <div className="flex-start">
                      <Typography className="fs14 font-medium black">
                        {selectedBill?.petShopInvoiceNumber ?? ""}
                      </Typography>
                    </div>
                    <div className="flex1-end">
                      <Typography className="txt-semi-bold fs14 orange3">
                        Rs{" "}
                        {Number(
                          Number(petShopTotal) -
                            Number(selectedBill?.petShopAmtPaid ?? 0),
                        )?.toFixed(2)}
                      </Typography>
                    </div>
                  </div>
                </div>
              </Grid>
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <CustomTextField
                  label="Amount"
                  name="petShopAmt"
                  fullWidth
                  handleChange={(e) =>
                    onChangeModValue("petShopAmt", e?.target?.value)
                  }
                  value={modValues?.petShopAmt}
                  helperText={modErrors?.petShopAmt}
                  error={!!modErrors?.petShopAmt}
                  labelTop
                  type="number"
                />
              </Grid>

              <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                <div className="font-medium fs14 black2">Payment Mode</div>
                <PaymentModeDropdown
                  list={[]}
                  value={modValues?.petShopPaymentMode}
                  error={!!modErrors?.petShopPaymentMode}
                  helperText={modErrors?.petShopPaymentMode}
                  onChange={(e, value) =>
                    onChangeModValue("petShopPaymentMode", e)
                  }
                  name="petShopPaymentMode"
                  label=""
                />
              </Grid>
              {/* {modValues?.petShopPaymentMode?.value === "Advance Adjustment" ? ( */}
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Typography className="txt-semi-bold fs14 orange5">
                  Available Credit:{" "}
                  {Number(selectedBill?.advanceAmount)?.toFixed(2)}
                </Typography>
              </Grid>
              {/* ) : null} */}
            </>
          ) : null}
          <div className="flex1-end mt20">
            <div>
              <CustomButton text="Pay" onClick={onPay} smallBtn />
            </div>
          </div>
        </Grid>
      </div>
    </CustomModal>
  );
};

export default CommonPayModal;
